import { useState, useEffect, useCallback } from 'react';
import { Product as TProduct } from '@core/types';

interface UseProductDetailCounterProps {
  product: any;
  selectedVariation: TProduct | null;
  basePrice: number;
  cartQuantity?: number;
}

export interface UseProductDetailCounterReturn {
  // State
  unitQuantity: number;
  packageQuantity: number;
  selectedType: 'unit' | 'package';
  selectedAttribute: any;
  
  // Handlers
  handleQuantityChange: (newQuantity: number, type: 'unit' | 'package') => void;
  handleTypeSelect: (type: 'unit' | 'package') => void;
  handleAttributeSelect: (attribute: any) => void;
  
  // Calculations
  getCurrentPrice: () => number;
  getCurrentQuantity: () => number;
  getTotalPrice: () => number;
  getFinalPrice: () => number;
  
  // Utils
  canAddToCart: () => boolean;
  resetQuantities: () => void;
  getMaxQuantity: () => number;
}

export const useProductDetailCounter = ({
  product,
  selectedVariation,
  basePrice,
  cartQuantity = 0
}: UseProductDetailCounterProps): UseProductDetailCounterReturn => {
  const [unitQuantity, setUnitQuantity] = useState<number>(1);
  const [packageQuantity, setPackageQuantity] = useState<number>(1);
  const [selectedType, setSelectedType] = useState<'unit' | 'package'>('unit');
  const [selectedAttribute, setSelectedAttribute] = useState<any>(null);

    // Ensure basePrice is always a number
  const safeBasePrice = Number(basePrice) || 0;

  const getMaxQuantity = useCallback(() => {
    const currentProduct = product?.type === "variable" ? selectedVariation : product;
    const stockQuantity = currentProduct?.stockQuantity;
    
    if (stockQuantity && stockQuantity > 0) {
      // Subtract already added items from cart to get remaining available quantity
      const remainingStock = stockQuantity - Number(cartQuantity || 0);
      return Math.max(0, remainingStock);
    }
    
    return 0;
  }, [product, selectedVariation, cartQuantity]);

  // Reset quantities when variation changes
  useEffect(() => {
    const maxQuantity = getMaxQuantity();
    setUnitQuantity(Math.min(1, maxQuantity));
    setPackageQuantity(Math.min(1, maxQuantity));
    setSelectedType('unit');
    
    // Auto-select first unit type attribute if available
    if (selectedVariation?.attributes && selectedVariation.attributes.length > 0) {
      const unitAttributes = selectedVariation.attributes.filter((attribute: any) => attribute.type === "unit");
      if (unitAttributes.length > 0) {
        setSelectedAttribute(unitAttributes[0]);
      } else {
        setSelectedAttribute(null);
      }
    } else {
      setSelectedAttribute(null);
    }
  }, [selectedVariation?.id, selectedVariation?.attributes, selectedVariation?.name, getMaxQuantity]);

  const handleQuantityChange = useCallback((newQuantity: number, type: 'unit' | 'package') => {
    if (newQuantity < 1) return;
    
    const maxQuantity = getMaxQuantity();
    const limitedQuantity = Math.min(newQuantity, maxQuantity);
    
    if (type === 'unit') {
      setUnitQuantity(limitedQuantity);
    } else {
      setPackageQuantity(limitedQuantity);
    }
  }, [getMaxQuantity]);

  const handleTypeSelect = useCallback((type: 'unit' | 'package') => {
    setSelectedType(type);
  }, []);

  const handleAttributeSelect = useCallback((attribute: any) => {
    setSelectedAttribute(attribute);
  }, []);

  const getCurrentPrice = useCallback(() => {
    // If no attribute selected, use base price
    if (!selectedAttribute) {
      return safeBasePrice;
    }
    
    // Apply attribute coefficient to base price if attribute is selected
    if (selectedAttribute.coefficient) {
      const coefficient = Number(selectedAttribute.coefficient) || 1;
      return Math.round(safeBasePrice * coefficient);
    }
    
    return safeBasePrice;
  }, [selectedAttribute, safeBasePrice]);

  const getCurrentQuantity = useCallback(() => {
    const quantity = selectedType === 'unit' ? unitQuantity : packageQuantity;
    return Number(quantity) || 1;
  }, [selectedType, unitQuantity, packageQuantity]);

  const getTotalPrice = useCallback(() => {
    const currentPrice = getCurrentPrice();
    const currentQuantity = getCurrentQuantity();
    return Number(currentPrice) * Number(currentQuantity);
  }, [getCurrentPrice, getCurrentQuantity]);

  const getFinalPrice = useCallback(() => {
    return Number(getTotalPrice()) || 0;
  }, [getTotalPrice]);

  const canAddToCart = useCallback(() => {
    // For unit type, we can add to cart if quantity > 0 and doesn't exceed stock
    const maxQuantity = getMaxQuantity();
    const currentQuantity = getCurrentQuantity();
    
    return currentQuantity > 0 && currentQuantity <= maxQuantity;
  }, [getMaxQuantity, getCurrentQuantity]);

  const resetQuantities = useCallback(() => {
    setUnitQuantity(1);
    setPackageQuantity(1);
    setSelectedAttribute(null);
    setSelectedType('unit');
  }, []);

  return {
    // State
    unitQuantity,
    packageQuantity,
    selectedType,
    selectedAttribute,
    
    // Handlers
    handleQuantityChange,
    handleTypeSelect,
    handleAttributeSelect,
    
    // Calculations
    getCurrentPrice,
    getCurrentQuantity,
    getTotalPrice,
    getFinalPrice,
    
    // Utils
    canAddToCart,
    resetQuantities,
    getMaxQuantity
  };
}; 